<template>
  <div>
    <b-card no-body>
      <b-card-header class="d-flex justify-content-between align-items-center flex-wrap">
        <b-card-title class="mb-0">
          실매매 전략비교
        </b-card-title>
        <div class="d-flex align-items-center flex-wrap">
          <div class="mr-1 text-muted small">
            서버시간: {{ dashboard.serverTime || '-' }}
          </div>
          <b-button
            variant="outline-primary"
            class="mr-1"
            :disabled="loading"
            @click="saveAutoTradeConfig"
          >
            설정 저장
          </b-button>
          <b-button
            variant="primary"
            :disabled="loading"
            @click="refreshMonitor(true)"
          >
            <b-spinner
              v-if="loading"
              small
              class="mr-50"
            />
            새로고침
          </b-button>
        </div>
      </b-card-header>

      <b-card-body>
        <b-alert
          v-if="message"
          :variant="messageVariant"
          show
        >
          {{ message }}
        </b-alert>

        <div class="strategy-compare-control-grid mb-2">
          <div class="control-box">
            <label class="form-label d-block mb-25">매매 ON/OFF</label>
            <b-form-checkbox
              v-model="filters.autoExecute"
              switch
            >
              {{ filters.autoExecute ? '백엔드 자동매매 ON' : '백엔드 자동매매 OFF' }}
            </b-form-checkbox>
          </div>
          <div class="control-box">
            <label class="form-label d-block mb-25">스케줄 상태</label>
            <div class="form-control bg-light">
              {{ runtimeStatus.trading ? '매매중' : (runtimeStatus.enabled ? '대기중' : 'OFF') }}
            </div>
          </div>
          <div class="control-box">
            <label class="form-label d-block mb-25">최근 처리</label>
            <div class="form-control bg-light">
              {{ runtimeStatus.lastCycleAt || runtimeStatus.lastResetAt || '-' }}
            </div>
          </div>
          <div class="control-box">
            <label class="form-label d-block mb-25">계좌번호</label>
            <b-form-select
              v-model="filters.accountNo"
              :options="accountOptions"
              :disabled="loadingAccounts"
            />
          </div>
          <div class="control-box">
            <label class="form-label d-block mb-25">거래소</label>
            <b-form-select
              v-model="filters.dmstStexTp"
              :options="domesticExchangeOptions"
            />
          </div>
          <div class="control-box">
            <label class="form-label d-block mb-25">시장구분</label>
            <b-form-select
              v-model="filters.rankMarketType"
              :options="marketOptions"
            />
          </div>
          <div class="control-box">
            <label class="form-label d-block mb-25">관리종목</label>
            <b-form-select
              v-model="filters.rankManageStockInclude"
              :options="manageOptions"
            />
          </div>
          <div class="control-box">
            <label class="form-label d-block mb-25">후보 개수</label>
            <b-form-input
              v-model.number="filters.candidateCount"
              type="number"
              min="5"
              max="20"
            />
          </div>
          <div class="control-box">
            <label class="form-label d-block mb-25">주문가능현금</label>
            <div class="form-control bg-light">
              {{ formatNumber(dashboard.orderableCash) }}
            </div>
          </div>
          <div class="control-box">
            <label class="form-label d-block mb-25">총 동시매수</label>
            <div class="form-control bg-light">
              {{ totalConcurrentBuyLimit }}
            </div>
          </div>
          <div class="control-box">
            <label class="form-label d-block mb-25">1건 매수금액</label>
            <div class="form-control bg-light">
              {{ formatNumber(estimatedPerTradeBudget) }}
            </div>
          </div>
        </div>

        <div class="strategy-toggle-grid mb-2">
          <div
            v-for="strategy in strategyToggles"
            :key="strategy.key"
            class="control-box"
          >
            <label class="form-label d-block mb-25">{{ strategy.label }}</label>
            <b-form-checkbox
              v-model="filters[strategy.key]"
              switch
            >
              {{ filters[strategy.key] ? 'ON' : 'OFF' }}
            </b-form-checkbox>
            <div class="mt-50">
              <label class="form-label d-block mb-25">동시매수</label>
              <b-form-input
                v-model.number="filters[strategy.limitKey]"
                type="number"
                min="1"
                max="10"
              />
            </div>
          </div>
        </div>

        <div class="strategy-card-row mb-2">
          <div
            v-for="card in dashboard.strategyCards || []"
            :key="card.strategyCode"
            class="strategy-card"
          >
            <div class="strategy-card-head">
              <strong>{{ card.strategyName }}</strong>
              <span class="strategy-card-status">{{ card.currentStatus }}</span>
            </div>
            <div class="strategy-card-body">
              <div class="small text-muted mb-50">{{ card.summary }}</div>
              <div><strong>{{ card.currentStockName || '-' }}</strong><span v-if="card.currentStockCode"> ({{ card.currentStockCode }})</span></div>
              <div class="small mt-50">진입가: {{ formatNumber(card.entryPrice) }}</div>
              <div class="small">현재가: {{ formatNumber(card.currentPrice) }}</div>
              <div class="small">현재손익률: <span :class="rateClassByValue(card.currentProfitRate)">{{ formatRate(card.currentProfitRate) }}</span></div>
              <div class="small">금일 매수/매도: {{ card.buyCountToday }}/{{ card.sellCountToday }}</div>
              <div class="small">실현손익: {{ formatSignedNumber(card.realizedProfitToday) }}</div>
              <div class="small">승률: {{ formatRate(card.winRateToday, false) }}</div>
            </div>
          </div>
        </div>

        <div class="strategy-tab-shell">
          <div class="strategy-date-picker">
            <label class="form-label mb-0 mr-50">조회일자</label>
            <b-form-input
              v-model="selectedTradeDate"
              type="date"
              class="strategy-date-input"
            />
          </div>
        <b-tabs pills nav-class="nav-pill-primary mb-2">
          <b-tab title="분석">
            <b-table
              :items="analysisStrategySummaries"
              :fields="analysisSummaryFields"
              responsive
              small
              show-empty
              empty-text="전략 분석 요약이 없습니다."
              class="mb-2"
            >
              <template #cell(realizedProfitAmount)="row">
                {{ formatSignedNumber(row.item.realizedProfitAmount) }}
              </template>
              <template #cell(averageProfitRate)="row">
                <span :class="rateClassByValue(row.item.averageProfitRate)">{{ formatRate(row.item.averageProfitRate) }}</span>
              </template>
              <template #cell(winRate)="row">
                {{ formatRate(row.item.winRate, false) }}
              </template>
              <template #cell(maxProfitRate)="row">
                <span :class="rateClassByValue(row.item.maxProfitRate)">{{ formatRate(row.item.maxProfitRate) }}</span>
              </template>
              <template #cell(minProfitRate)="row">
                <span :class="rateClassByValue(row.item.minProfitRate)">{{ formatRate(row.item.minProfitRate) }}</span>
              </template>
              <template #bottom-row>
                <td class="font-weight-bold analysis-total-row">
                  합계
                </td>
                <td class="font-weight-bold analysis-total-row">
                  {{ formatNumber(analysisSummaryTotal.tradeCount) }}
                </td>
                <td class="font-weight-bold analysis-total-row">
                  {{ formatNumber(analysisSummaryTotal.closedTradeCount) }}
                </td>
                <td class="font-weight-bold analysis-total-row">
                  <span :class="rateClassByValue(analysisSummaryTotal.realizedProfitAmount)">
                    {{ formatSignedNumber(analysisSummaryTotal.realizedProfitAmount) }}
                  </span>
                </td>
                <td class="font-weight-bold analysis-total-row">
                  <span :class="rateClassByValue(analysisSummaryTotal.averageProfitRate)">
                    {{ formatRate(analysisSummaryTotal.averageProfitRate) }}
                  </span>
                </td>
                <td class="font-weight-bold analysis-total-row">
                  -
                </td>
                <td class="font-weight-bold analysis-total-row">
                  {{ formatRate(analysisSummaryTotal.winRate, false) }}
                </td>
                <td class="font-weight-bold analysis-total-row">
                  <span :class="rateClassByValue(analysisSummaryTotal.maxProfitRate)">
                    {{ formatRate(analysisSummaryTotal.maxProfitRate) }}
                  </span>
                </td>
                <td class="font-weight-bold analysis-total-row">
                  <span :class="rateClassByValue(analysisSummaryTotal.minProfitRate)">
                    {{ formatRate(analysisSummaryTotal.minProfitRate) }}
                  </span>
                </td>
              </template>
              <template #row-details="row">
                <div class="strategy-trade-subtable">
                  <div class="small font-weight-bold mb-50">
                    금일 매매종목
                  </div>
                  <b-table
                    :items="row.item.todayTrades || []"
                    :fields="analysisTradeFields"
                    responsive
                    small
                    show-empty
                    empty-text="금일 매매종목이 없습니다."
                  >
                    <template #cell(statusName)="tradeRow">
                      <b-badge :variant="tradeStatusVariant(tradeRow.item.status)">
                        {{ tradeRow.item.statusName }}
                      </b-badge>
                    </template>
                    <template #cell(orderQuantity)="tradeRow">
                      {{ formatNumber(tradeRow.item.orderQuantity) }}
                    </template>
                    <template #cell(holdingQuantity)="tradeRow">
                      {{ formatNumber(tradeRow.item.holdingQuantity) }}
                    </template>
                    <template #cell(holdingAmount)="tradeRow">
                      {{ formatNumber(tradeRow.item.holdingAmount) }}
                    </template>
                    <template #cell(profitRate)="tradeRow">
                      <span :class="rateClassByValue(tradeRow.item.profitRate)">{{ formatRate(tradeRow.item.profitRate) }}</span>
                    </template>
                    <template #cell(realizedProfitAmount)="tradeRow">
                      <span :class="rateClassByValue(tradeRow.item.realizedProfitAmount)">{{ formatSignedNumber(tradeRow.item.realizedProfitAmount) }}</span>
                    </template>
                  </b-table>
                </div>
              </template>
            </b-table>

            <h6 class="mb-1">최근 이벤트</h6>
            <b-table
              :items="analysis.recentEvents || []"
              :fields="analysisEventFields"
              responsive
              small
              show-empty
              empty-text="최근 이벤트가 없습니다."
              class="mb-2"
            >
              <template #cell(price)="row">
                {{ formatNumber(row.item.price) }}
              </template>
              <template #cell(profitRate)="row">
                <span :class="rateClassByValue(row.item.profitRate)">{{ formatRate(row.item.profitRate) }}</span>
              </template>
            </b-table>

            <h6 class="mb-1">최근 스냅샷</h6>
            <b-table
              :items="analysis.recentSnapshots || []"
              :fields="analysisSnapshotFields"
              responsive
              small
              show-empty
              empty-text="최근 스냅샷이 없습니다."
            >
              <template #cell(currentPrice)="row">
                {{ formatNumber(row.item.currentPrice) }}
              </template>
              <template #cell(profitRate)="row">
                <span :class="rateClassByValue(row.item.profitRate)">{{ formatRate(row.item.profitRate) }}</span>
              </template>
              <template #cell(tradeAmount)="row">
                {{ formatNumber(row.item.tradeAmount) }}
              </template>
              <template #cell(fluctuationRate)="row">
                <span :class="rateClassByValue(row.item.fluctuationRate)">{{ formatRate(row.item.fluctuationRate) }}</span>
              </template>
            </b-table>
          </b-tab>

          <b-tab title="후보 점검">
            <b-table
              :items="displayedCandidateReviews"
              :fields="candidateReviewFields"
              responsive
              small
              show-empty
              empty-text="후보 점검 데이터가 없습니다."
            >
              <template #cell(currentPrice)="row">
                {{ formatNumber(row.item.currentPrice) }}
              </template>
              <template #cell(tradeAmount)="row">
                {{ formatNumber(row.item.tradeAmount) }}
              </template>
              <template #cell(fluctuationRate)="row">
                <span :class="rateClassByValue(row.item.fluctuationRate)">{{ formatRate(row.item.fluctuationRate) }}</span>
              </template>
              <template #cell(turnoverRatio)="row">
                {{ formatRate(row.item.turnoverRatio, false) }}
              </template>
              <template #cell(volumeAcceleration)="row">
                {{ formatNumber(row.item.volumeAcceleration) }}
              </template>
            </b-table>
          </b-tab>

          <b-tab title="최적화 보고서">
            <div class="d-flex flex-wrap align-items-center mb-1">
              <b-button
                variant="outline-primary"
                class="mr-1 mb-50"
                :disabled="optimizationLoading"
                @click="fetchOptimizationReports"
              >
                <b-spinner
                  v-if="optimizationLoading"
                  small
                  class="mr-50"
                />
                보고서 조회
              </b-button>
              <b-button
                variant="primary"
                class="mb-50"
                :disabled="optimizationLoading"
                @click="runOptimization"
              >
                즉시 분석
              </b-button>
            </div>
            <b-table
              :items="optimizationReports"
              :fields="optimizationReportFields"
              responsive
              small
              show-empty
              empty-text="생성된 최적화 보고서가 없습니다."
              class="mb-2"
              @row-clicked="openOptimizationReport"
            >
              <template #cell(totalProfitAmount)="row">
                {{ formatSignedNumber(row.item.totalProfitAmount) }}
              </template>
              <template #cell(averageProfitRate)="row">
                <span :class="rateClassByValue(row.item.averageProfitRate)">{{ formatRate(row.item.averageProfitRate) }}</span>
              </template>
            </b-table>

            <div v-if="optimizationDetail.summary">
              <h6 class="mb-1">{{ optimizationDetail.summary.reportTitle }}</h6>
              <b-table
                :items="optimizationDetail.strategyMetrics || []"
                :fields="optimizationMetricFields"
                responsive
                small
                show-empty
                empty-text="전략별 최적화 지표가 없습니다."
                class="mb-2"
              >
                <template #cell(realizedProfitAmount)="row">
                  {{ formatSignedNumber(row.item.realizedProfitAmount) }}
                </template>
                <template #cell(averageProfitRate)="row">
                  <span :class="rateClassByValue(row.item.averageProfitRate)">{{ formatRate(row.item.averageProfitRate) }}</span>
                </template>
                <template #cell(winRate)="row">
                  {{ formatRate(row.item.winRate, false) }}
                </template>
              </b-table>
              <pre class="optimization-report-markdown">{{ optimizationDetail.reportMarkdown }}</pre>
            </div>
          </b-tab>
        </b-tabs>
        </div>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import axios from '@axios'
import {
  BAlert,
  BBadge,
  BButton,
  BCard,
  BCardBody,
  BCardHeader,
  BCardTitle,
  BFormCheckbox,
  BFormInput,
  BFormSelect,
  BSpinner,
  BTab,
  BTable,
  BTabs,
} from 'bootstrap-vue'

export default {
  components: {
    BAlert,
    BBadge,
    BButton,
    BCard,
    BCardBody,
    BCardHeader,
    BCardTitle,
    BFormCheckbox,
    BFormInput,
    BFormSelect,
    BSpinner,
    BTab,
    BTable,
    BTabs,
  },
  data() {
    const today = this.todayInput()
    return {
      loading: false,
      loadingAccounts: false,
      historyLoading: false,
      optimizationLoading: false,
      selectedTradeDate: today,
      autoRefreshTimer: null,
      syncingRuntimeStatus: false,
      message: '',
      messageVariant: 'danger',
      accounts: [],
      filters: {
        accountNo: '',
        dmstStexTp: 'KRX',
        rankMarketType: '000',
        rankManageStockInclude: '0',
        candidateCount: 12,
        fixedPerTradeBudget: 0,
        autoExecute: false,
        supportLineEnabled: true,
        supportLineMaxConcurrent: 3,
        vwapPullbackEnabled: true,
        vwapPullbackMaxConcurrent: 1,
        orbVwapEnabled: true,
        orbVwapMaxConcurrent: 1,
      },
      dashboard: {
        serverTime: '',
        orderableCash: '',
        candidateReviews: [],
        strategyCards: [],
        openTrades: [],
        todayStats: [],
      },
      runtimeStatus: {
        enabled: false,
        trading: false,
        lastStartedDate: '',
        lastResetAt: '',
        lastCycleAt: '',
        lastStoppedAt: '',
        lastError: '',
        message: '',
      },
      analysis: {
        strategySummaries: [],
        recentEvents: [],
        recentSnapshots: [],
      },
      historyFilter: {
        fromDate: today,
        toDate: today,
      },
      historyStats: [],
      selectedCandidateReviews: [],
      optimizationReports: [],
      optimizationDetail: {
        summary: null,
        reportMarkdown: '',
        recommendationsJson: '',
        aiCommentary: '',
        aiError: '',
        strategyMetrics: [],
      },
      marketOptions: [
        { value: '000', text: '전체' },
        { value: '001', text: '코스피' },
        { value: '101', text: '코스닥' },
      ],
      manageOptions: [
        { value: '0', text: '미포함' },
        { value: '1', text: '포함' },
      ],
      domesticExchangeOptions: [
        { value: 'KRX', text: 'KRX' },
        { value: 'NXT', text: 'NXT' },
        { value: 'SOR', text: 'SOR' },
      ],
      openTradeFields: [
        { key: 'strategyName', label: '전략' },
        { key: 'stockName', label: '종목명' },
        { key: 'status', label: '상태' },
        { key: 'buyRequestedAt', label: '매수시간' },
        { key: 'buyOrderNo', label: '매수주문번호' },
        { key: 'buyPrice', label: '매수가' },
        { key: 'currentPrice', label: '현재가' },
        { key: 'currentProfitRate', label: '현재손익률' },
        { key: 'sellRule', label: '매도규칙' },
      ],
      candidateReviewFields: [
        { key: 'currentRank', label: '순위' },
        { key: 'stockName', label: '종목명' },
        { key: 'currentPrice', label: '현재가' },
        { key: 'tradeAmount', label: '거래대금' },
        { key: 'fluctuationRate', label: '등락률' },
        { key: 'turnoverRatio', label: '시총대비회전율' },
        { key: 'volumeAcceleration', label: '거래량가속' },
        { key: 'reviewStatus', label: '상태' },
        { key: 'eligibleStrategies', label: '매수가능전략' },
        { key: 'excludeReason', label: '제외/대기사유' },
      ],
      statFields: [
        { key: 'strategyName', label: '전략' },
        { key: 'buyCount', label: '매수건수' },
        { key: 'sellCount', label: '매도건수' },
        { key: 'closedTradeCount', label: '종결건수' },
        { key: 'openTradeCount', label: '오픈건수' },
        { key: 'realizedProfitAmount', label: '실현손익' },
        { key: 'winCount', label: '승' },
        { key: 'lossCount', label: '패' },
        { key: 'winRate', label: '승률' },
      ],
      historyFields: [
        { key: 'tradeDate', label: '일자' },
        { key: 'strategyName', label: '전략' },
        { key: 'buyCount', label: '매수건수' },
        { key: 'sellCount', label: '매도건수' },
        { key: 'closedTradeCount', label: '종결건수' },
        { key: 'openTradeCount', label: '오픈건수' },
        { key: 'realizedProfitAmount', label: '실현손익' },
        { key: 'winCount', label: '승' },
        { key: 'lossCount', label: '패' },
        { key: 'winRate', label: '승률' },
      ],
      analysisSummaryFields: [
        { key: 'strategyName', label: '전략' },
        { key: 'tradeCount', label: '총매매' },
        { key: 'closedTradeCount', label: '종결건수' },
        { key: 'realizedProfitAmount', label: '실현손익' },
        { key: 'averageProfitRate', label: '평균손익률' },
        { key: 'averageHoldingMinutes', label: '평균보유분' },
        { key: 'winRate', label: '승률' },
        { key: 'maxProfitRate', label: '최대손익률' },
        { key: 'minProfitRate', label: '최소손익률' },
      ],
      analysisTradeFields: [
        { key: 'stockName', label: '종목명' },
        { key: 'statusName', label: '상태' },
        { key: 'orderQuantity', label: '주문량' },
        { key: 'holdingQuantity', label: '보유량' },
        { key: 'buyPrice', label: '매수가' },
        { key: 'currentPrice', label: '현재가' },
        { key: 'sellPrice', label: '매도가' },
        { key: 'holdingAmount', label: '보유금액' },
        { key: 'profitRate', label: '손익률' },
        { key: 'realizedProfitAmount', label: '실현손익' },
        { key: 'buyRequestedAt', label: '매수시각' },
        { key: 'sellRequestedAt', label: '매도시각' },
      ],
      analysisEventFields: [
        { key: 'createdAt', label: '시각' },
        { key: 'strategyName', label: '전략' },
        { key: 'stockName', label: '종목명' },
        { key: 'eventType', label: '이벤트' },
        { key: 'eventMessage', label: '내용' },
        { key: 'orderNo', label: '주문번호' },
        { key: 'price', label: '가격' },
        { key: 'profitRate', label: '손익률' },
      ],
      analysisSnapshotFields: [
        { key: 'capturedAt', label: '분시각' },
        { key: 'strategyName', label: '전략' },
        { key: 'stockName', label: '종목명' },
        { key: 'status', label: '상태' },
        { key: 'currentPrice', label: '현재가' },
        { key: 'profitRate', label: '손익률' },
        { key: 'currentRank', label: '순위' },
        { key: 'tradeAmount', label: '거래대금' },
        { key: 'fluctuationRate', label: '등락률' },
      ],
      optimizationReportFields: [
        { key: 'runDate', label: '분석일' },
        { key: 'status', label: '상태' },
        { key: 'finishedAt', label: '완료시각' },
        { key: 'tradeCount', label: '전체매매' },
        { key: 'closedTradeCount', label: '종결매매' },
        { key: 'totalProfitAmount', label: '실현손익' },
        { key: 'averageProfitRate', label: '평균수익률' },
        { key: 'aiUsed', label: 'AI' },
      ],
      optimizationMetricFields: [
        { key: 'strategyName', label: '전략' },
        { key: 'tradeCount', label: '전체매매' },
        { key: 'closedTradeCount', label: '종결매매' },
        { key: 'winRate', label: '승률' },
        { key: 'realizedProfitAmount', label: '실현손익' },
        { key: 'averageProfitRate', label: '평균수익률' },
        { key: 'averageHoldingMinutes', label: '평균보유분' },
        { key: 'suggestion', label: '제안' },
      ],
      strategyToggles: [
        { key: 'supportLineEnabled', limitKey: 'supportLineMaxConcurrent', label: '지지선 눌림+스토캐스틱' },
        { key: 'vwapPullbackEnabled', limitKey: 'vwapPullbackMaxConcurrent', label: 'VWAP + EMA5/20 + 거래량' },
        { key: 'orbVwapEnabled', limitKey: 'orbVwapMaxConcurrent', label: 'ORB + 거래량 + VWAP' },
      ],
    }
  },
  watch: {
    selectedTradeDate() {
      this.fetchSelectedDateData()
    },
    'filters.autoExecute'(value) {
      if (this.syncingRuntimeStatus) return
      if (value) {
        this.turnAutoTradeOn()
        return
      }
      this.turnAutoTradeOff()
    },
  },
  created() {
    this.fetchAccounts()
    this.fetchSelectedDateData()
    this.startAutoRefresh()
  },
  beforeDestroy() {
    this.stopAutoRefresh()
  },
  methods: {
    parseApiResponse(response) {
      const payload = response && response.data ? response.data : null
      if (!payload || payload.success !== true) {
        throw new Error(payload && payload.error ? payload.error : '요청 처리 중 오류가 발생했습니다.')
      }
      return payload.data
    },
    todayInput() {
      const now = new Date()
      const yyyy = now.getFullYear()
      const mm = String(now.getMonth() + 1).padStart(2, '0')
      const dd = String(now.getDate()).padStart(2, '0')
      return `${yyyy}-${mm}-${dd}`
    },
    compactDate(value) {
      return (value || '').replace(/-/g, '')
    },
    selectedCompactDate() {
      return this.compactDate(this.selectedTradeDate || this.todayInput())
    },
    isSelectedToday() {
      return this.selectedCompactDate() === this.compactDate(this.todayInput())
    },
    async fetchSelectedDateData() {
      await Promise.all([
        this.fetchAnalysis(),
        this.fetchCandidateReviews(),
        this.fetchOptimizationReports(),
      ])
    },
    async fetchAccounts() {
      this.loadingAccounts = true
      try {
        const response = await axios.post('/api/kiwoom/account/list', {})
        const data = this.parseApiResponse(response)
        this.accounts = Array.isArray(data.accounts) ? data.accounts : []
        this.filters.accountNo = this.accounts[0] || ''
        await this.refreshMonitor(false)
      } catch (error) {
        this.messageVariant = 'danger'
        this.message = error && error.message ? error.message : '계좌번호 조회에 실패했습니다.'
      } finally {
        this.loadingAccounts = false
      }
    },
    buildDashboardRequest(autoExecute = false) {
      return {
        accountNo: this.filters.accountNo,
        dmstStexTp: this.filters.dmstStexTp,
        rankMarketType: this.filters.rankMarketType,
        rankManageStockInclude: this.filters.rankManageStockInclude,
        rankExchangeType: this.filters.dmstStexTp === 'NXT' ? '2' : this.filters.dmstStexTp === 'SOR' ? '3' : '1',
        candidateCount: this.filters.candidateCount,
        fixedPerTradeBudget: this.filters.fixedPerTradeBudget,
        autoExecute,
        supportLineEnabled: this.filters.supportLineEnabled,
        supportLineMaxConcurrent: this.filters.supportLineMaxConcurrent,
        vwapPullbackEnabled: this.filters.vwapPullbackEnabled,
        vwapPullbackMaxConcurrent: this.filters.vwapPullbackMaxConcurrent,
        orbVwapEnabled: this.filters.orbVwapEnabled,
        orbVwapMaxConcurrent: this.filters.orbVwapMaxConcurrent,
      }
    },
    async fetchDashboard(showLoading = true) {
      if (showLoading) {
        this.loading = true
      }
      this.message = ''
      try {
        const response = await axios.post('/api/kiwoom/strategy-compare/dashboard', this.buildDashboardRequest(false))
        const data = this.parseApiResponse(response)
        this.dashboard = data || {}
      } catch (error) {
        this.messageVariant = 'danger'
        this.message = error && error.message ? error.message : '실매매 전략비교 조회에 실패했습니다.'
      } finally {
        if (showLoading) {
          this.loading = false
        }
      }
    },
    applyRuntimeStatus(status) {
      this.runtimeStatus = status || this.runtimeStatus
      if (status && status.config) {
        const nextConfig = {
          ...status.config,
          accountNo: status.config.accountNo || this.filters.accountNo,
        }
        this.syncingRuntimeStatus = true
        this.filters = {
          ...this.filters,
          ...nextConfig,
          autoExecute: !!status.enabled,
        }
        this.$nextTick(() => {
          this.syncingRuntimeStatus = false
        })
      } else {
        this.syncingRuntimeStatus = true
        this.filters.autoExecute = !!(status && status.enabled)
        this.$nextTick(() => {
          this.syncingRuntimeStatus = false
        })
      }
      if (status && status.dashboard) {
        this.dashboard = status.dashboard
      }
      if (status && status.lastError) {
        this.messageVariant = 'danger'
        this.message = status.lastError
      } else if (status && status.message) {
        this.messageVariant = status.enabled ? 'success' : 'warning'
        this.message = status.message
      }
    },
    async refreshMonitor(showLoading = false) {
      if (showLoading) {
        this.loading = true
      }
      try {
        const response = await axios.post('/api/kiwoom/strategy-compare/auto-trade/status', {})
        const data = this.parseApiResponse(response)
        this.applyRuntimeStatus(data)
        await this.fetchAnalysis()
      } catch (error) {
        this.messageVariant = 'danger'
        this.message = error && error.message ? error.message : '자동매매 상태 조회에 실패했습니다.'
      } finally {
        if (showLoading) {
          this.loading = false
        }
      }
    },
    async saveAutoTradeConfig() {
      this.loading = true
      try {
        const response = await axios.post('/api/kiwoom/strategy-compare/auto-trade/config', this.buildDashboardRequest(false))
        const data = this.parseApiResponse(response)
        this.applyRuntimeStatus(data)
      } catch (error) {
        this.messageVariant = 'danger'
        this.message = error && error.message ? error.message : '자동매매 설정 저장에 실패했습니다.'
      } finally {
        this.loading = false
      }
    },
    async turnAutoTradeOn() {
      this.loading = true
      try {
        const response = await axios.post('/api/kiwoom/strategy-compare/auto-trade/on', this.buildDashboardRequest(false))
        const data = this.parseApiResponse(response)
        this.applyRuntimeStatus(data)
      } catch (error) {
        this.messageVariant = 'danger'
        this.message = error && error.message ? error.message : '자동매매 ON 전환에 실패했습니다.'
      } finally {
        this.loading = false
      }
    },
    async turnAutoTradeOff() {
      this.loading = true
      try {
        const response = await axios.post('/api/kiwoom/strategy-compare/auto-trade/off', {})
        const data = this.parseApiResponse(response)
        this.applyRuntimeStatus(data)
      } catch (error) {
        this.messageVariant = 'danger'
        this.message = error && error.message ? error.message : '자동매매 OFF 전환에 실패했습니다.'
      } finally {
        this.loading = false
      }
    },
    async fetchHistory() {
      this.historyLoading = true
      try {
        const response = await axios.post('/api/kiwoom/strategy-compare/history', {
          fromDate: this.compactDate(this.historyFilter.fromDate),
          toDate: this.compactDate(this.historyFilter.toDate),
        })
        const data = this.parseApiResponse(response)
        this.historyStats = Array.isArray(data.stats) ? data.stats : []
        await this.fetchAnalysis()
      } catch (error) {
        this.messageVariant = 'danger'
        this.message = error && error.message ? error.message : '전략 이력 조회에 실패했습니다.'
      } finally {
        this.historyLoading = false
      }
    },
    async fetchAnalysis() {
      try {
        const tradeDate = this.selectedCompactDate()
        const response = await axios.post('/api/kiwoom/strategy-compare/analysis', {
          fromDate: tradeDate,
          toDate: tradeDate,
        })
        const data = this.parseApiResponse(response)
        this.analysis = data || {
          strategySummaries: [],
          recentEvents: [],
          recentSnapshots: [],
        }
      } catch (error) {
        this.messageVariant = 'danger'
        this.message = error && error.message ? error.message : '전략 분석 조회에 실패했습니다.'
      }
    },
    async fetchCandidateReviews() {
      try {
        const tradeDate = this.selectedCompactDate()
        const response = await axios.post('/api/kiwoom/strategy-compare/candidate-reviews', {
          fromDate: tradeDate,
          toDate: tradeDate,
        })
        const data = this.parseApiResponse(response)
        this.selectedCandidateReviews = Array.isArray(data.candidateReviews) ? data.candidateReviews : []
      } catch (error) {
        this.messageVariant = 'danger'
        this.message = error && error.message ? error.message : '후보 점검 조회에 실패했습니다.'
      }
    },
    async fetchOptimizationReports() {
      this.optimizationLoading = true
      try {
        const response = await axios.post('/api/kiwoom/strategy-compare/optimization/reports', {
          runDate: this.selectedCompactDate(),
        })
        const data = this.parseApiResponse(response)
        this.optimizationReports = Array.isArray(data.reports) ? data.reports : []
        this.optimizationDetail = {
          summary: null,
          reportMarkdown: '',
          recommendationsJson: '',
          aiCommentary: '',
          aiError: '',
          strategyMetrics: [],
        }
        if (this.optimizationReports.length) {
          await this.openOptimizationReport(this.optimizationReports[0])
        }
      } catch (error) {
        this.messageVariant = 'danger'
        this.message = error && error.message ? error.message : '최적화 보고서 조회에 실패했습니다.'
      } finally {
        this.optimizationLoading = false
      }
    },
    async openOptimizationReport(item) {
      if (!item || !item.id) return
      this.optimizationLoading = true
      try {
        const response = await axios.post('/api/kiwoom/strategy-compare/optimization/report', {
          runDate: item.id,
        })
        this.optimizationDetail = this.parseApiResponse(response) || this.optimizationDetail
      } catch (error) {
        this.messageVariant = 'danger'
        this.message = error && error.message ? error.message : '최적화 보고서 상세 조회에 실패했습니다.'
      } finally {
        this.optimizationLoading = false
      }
    },
    async runOptimization() {
      this.optimizationLoading = true
      try {
        const response = await axios.post('/api/kiwoom/strategy-compare/optimization/run', {
          runDate: this.selectedCompactDate(),
          useAi: true,
        })
        this.optimizationDetail = this.parseApiResponse(response) || this.optimizationDetail
        await this.fetchOptimizationReports()
        this.messageVariant = 'success'
        this.message = '전략 최적화 분석 보고서를 생성했습니다.'
      } catch (error) {
        this.messageVariant = 'danger'
        this.message = error && error.message ? error.message : '전략 최적화 분석에 실패했습니다.'
      } finally {
        this.optimizationLoading = false
      }
    },
    startAutoRefresh() {
      this.stopAutoRefresh()
      this.autoRefreshTimer = setInterval(() => {
        this.refreshMonitor(false)
      }, 30000)
    },
    stopAutoRefresh() {
      if (this.autoRefreshTimer) {
        clearInterval(this.autoRefreshTimer)
        this.autoRefreshTimer = null
      }
    },
    toNumber(value) {
      if (value === null || value === undefined || value === '') return null
      const normalized = String(value).replace(/,/g, '').replace(/\+/g, '')
      const parsed = Number(normalized)
      return Number.isNaN(parsed) ? null : parsed
    },
    formatNumber(value) {
      const numeric = this.toNumber(value)
      if (numeric === null) return '-'
      return numeric.toLocaleString()
    },
    formatSignedNumber(value) {
      const numeric = this.toNumber(value)
      if (numeric === null) return '-'
      if (numeric > 0) return `+${numeric.toLocaleString()}`
      return numeric.toLocaleString()
    },
    formatRate(value, withSign = true) {
      const numeric = this.toNumber(value)
      if (numeric === null) return '-'
      const prefix = withSign && numeric > 0 ? '+' : ''
      return `${prefix}${numeric.toFixed(2)}%`
    },
    rateClassByValue(value) {
      const numeric = this.toNumber(value)
      if (numeric === null) return 'text-body'
      if (numeric > 0) return 'text-danger'
      if (numeric < 0) return 'text-primary'
      return 'text-body'
    },
    tradeStatusVariant(status) {
      if (status === 'BUY_ORDERED') return 'light-warning'
      if (status === 'BOUGHT') return 'light-primary'
      if (status === 'SELL_ORDERED') return 'light-info'
      if (status === 'CLOSED') return 'light-success'
      if (status === 'CANCELLED' || status === 'RESET') return 'light-secondary'
      return 'light-dark'
    },
  },
  computed: {
    analysisStrategySummaries() {
      return (this.analysis.strategySummaries || []).map(item => ({
        ...item,
        _showDetails: true,
      }))
    },
    displayedCandidateReviews() {
      if (this.isSelectedToday()) {
        return this.dashboard.candidateReviews || []
      }
      return this.selectedCandidateReviews || []
    },
    analysisSummaryTotal() {
      const summaries = this.analysis.strategySummaries || []
      const total = {
        tradeCount: 0,
        closedTradeCount: 0,
        realizedProfitAmount: 0,
        averageProfitRate: 0,
        winRate: 0,
        maxProfitRate: null,
        minProfitRate: null,
      }
      let weightedProfitRate = 0
      let weightedWinRate = 0

      summaries.forEach(item => {
        const tradeCount = this.toNumber(item.tradeCount) || 0
        const closedTradeCount = this.toNumber(item.closedTradeCount) || 0
        const realizedProfitAmount = this.toNumber(item.realizedProfitAmount) || 0
        const averageProfitRate = this.toNumber(item.averageProfitRate) || 0
        const winRate = this.toNumber(item.winRate) || 0
        const maxProfitRate = this.toNumber(item.maxProfitRate)
        const minProfitRate = this.toNumber(item.minProfitRate)

        total.tradeCount += tradeCount
        total.closedTradeCount += closedTradeCount
        total.realizedProfitAmount += realizedProfitAmount
        weightedProfitRate += averageProfitRate * closedTradeCount
        weightedWinRate += winRate * closedTradeCount

        if (closedTradeCount > 0 && maxProfitRate !== null) {
          total.maxProfitRate = total.maxProfitRate === null ? maxProfitRate : Math.max(total.maxProfitRate, maxProfitRate)
        }
        if (closedTradeCount > 0 && minProfitRate !== null) {
          total.minProfitRate = total.minProfitRate === null ? minProfitRate : Math.min(total.minProfitRate, minProfitRate)
        }
      })

      if (total.closedTradeCount > 0) {
        total.averageProfitRate = weightedProfitRate / total.closedTradeCount
        total.winRate = weightedWinRate / total.closedTradeCount
      }
      total.maxProfitRate = total.maxProfitRate === null ? 0 : total.maxProfitRate
      total.minProfitRate = total.minProfitRate === null ? 0 : total.minProfitRate
      return total
    },
    totalConcurrentBuyLimit() {
      let total = 0
      if (this.filters.supportLineEnabled) total += Number(this.filters.supportLineMaxConcurrent || 0)
      if (this.filters.vwapPullbackEnabled) total += Number(this.filters.vwapPullbackMaxConcurrent || 0)
      if (this.filters.orbVwapEnabled) total += Number(this.filters.orbVwapMaxConcurrent || 0)
      return total
    },
    estimatedPerTradeBudget() {
      if (Number(this.filters.fixedPerTradeBudget || 0) > 0) {
        return Number(this.filters.fixedPerTradeBudget || 0)
      }
      const cash = Number((this.dashboard.orderableCash || '0').toString().replace(/,/g, '')) || 0
      const total = this.totalConcurrentBuyLimit
      if (cash <= 0 || total <= 0) return 0
      return Math.floor(cash / total)
    },
    accountOptions() {
      if (!this.accounts.length) {
        return [{ value: '', text: '계좌 없음' }]
      }
      return this.accounts.map(account => ({
        value: account,
        text: account,
      }))
    },
  },
}
</script>

<style lang="scss" scoped>
.strategy-compare-control-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 0.75rem;
}

.strategy-toggle-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 0.75rem;
}

.control-box,
.strategy-card {
  padding: 0.85rem 1rem;
  border: 1px solid rgba(34, 41, 47, 0.08);
  border-radius: 0.428rem;
  background: #fff;
}

.strategy-card-row {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 0.75rem;
}

.strategy-card-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.strategy-card-status {
  font-size: 0.75rem;
  color: #7367f0;
  font-weight: 700;
}

.strategy-card-body {
  font-size: 0.83rem;
  line-height: 1.5;
}

.strategy-tab-shell {
  position: relative;
}

.strategy-date-picker {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 2;
  display: flex;
  align-items: center;
}

.strategy-date-input {
  width: 150px;
}

.analysis-total-row {
  background: #f8f8fb;
  border-top: 2px solid #ebe9f1;
}

.optimization-report-markdown {
  white-space: pre-wrap;
  word-break: keep-all;
  background: #f8f8f8;
  border: 1px solid #ebe9f1;
  border-radius: 0.25rem;
  padding: 1rem;
  max-height: 560px;
  overflow: auto;
}

.strategy-trade-subtable {
  margin: 0.25rem 0 0.75rem;
  padding: 0.75rem;
  border-left: 3px solid #7367f0;
  background: #fbfbfd;
}

@media (max-width: 1400px) {
  .strategy-toggle-grid,
  .strategy-card-row {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 900px) {
  .strategy-toggle-grid,
  .strategy-card-row {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }

  .strategy-date-picker {
    position: static;
    justify-content: flex-end;
    margin-bottom: 0.75rem;
  }
}
</style>
